import React, { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useServices } from '../context/ServiceContext';
import './ToolsPanel.css';

const TOOLS = [
  { id:'services',  label:'Service Types',    desc:'Manage your service catalogue' },
  { id:'import',    label:'Import Leads',     desc:'Upload CSV file' },
  { id:'export',    label:'Export Leads',     desc:'Download as CSV' },
  { id:'exportall', label:'Export All Data',  desc:'Full data backup JSON' },
  { id:'notes',     label:'Quick Notes',      desc:'Scratch pad for ideas' },
  { id:'invoice',   label:'Invoice Generator',desc:'Create & print invoices' },
];

function ServiceTypes() {
  const { services, add, remove, edit } = useServices();
  const [newSvc, setNewSvc] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Service Types <span className="tp-badge">{services.length}</span></div>
      <div className="tp-svc-list">
        {services.map(s=>(
          <div key={s} className="tp-svc-item">
            {editing===s?(
              <input className="tp-svc-inp" value={editVal} autoFocus onChange={e=>setEditVal(e.target.value)}
                onBlur={()=>{edit(s,editVal);setEditing(null);}}
                onKeyDown={e=>{if(e.key==='Enter'){edit(s,editVal);setEditing(null);}if(e.key==='Escape')setEditing(null);}}/>
            ):(
              <span className="tp-svc-name" onClick={()=>{setEditing(s);setEditVal(s);}}>{s}</span>
            )}
            <div className="tp-svc-actions">
              <div className="tp-svc-btn" onClick={()=>{setEditing(s);setEditVal(s);}}>✏</div>
              <div className="tp-svc-btn tp-svc-del" onClick={()=>remove(s)}>✕</div>
            </div>
          </div>
        ))}
      </div>
      <div className="tp-svc-add-row">
        <input className="tp-svc-add-inp" placeholder="Add new service type…" value={newSvc}
          onChange={e=>setNewSvc(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'){add(newSvc);setNewSvc('');}}}/>
        <div className="tp-svc-add-btn" onClick={()=>{add(newSvc);setNewSvc('');}}>+ Add</div>
      </div>
    </div>
  );
}

function ImportLeads() {
  const fileRef = useRef();
  const [status, setStatus] = useState('');
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStatus(`"${file.name}" selected — go to Leads page to import`);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Import Leads</div>
      <div className="tp-upload-zone" onClick={()=>fileRef.current.click()}>
        <div className="tp-upload-label">Click to select CSV file</div>
        <div className="tp-upload-hint">Use the Import button on the Leads page for full import</div>
        <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleFile}/>
      </div>
      {status && <div className="tp-status">{status}</div>}
      <a className="tp-template-link" href="data:text/csv,Full Name,Phone Number,Lead Type,Business Type,Lead Quality,Budget,Status,Follow Up 1,Notes 1" download="leads_template.csv">
        Download CSV template
      </a>
    </div>
  );
}

function ExportLeads() {
  const handleExport = () => {
    try {
      const leads = JSON.parse(localStorage.getItem('ryzenos_leads')||'[]');
      const headers = ['Name','Company','Phone','Email','Source','Status','Budget','Service','Location','Assigned','Follow Up','Notes'];
      const rows = leads.map(l=>[l.name,l.company,l.phone,l.email,l.source,l.status,l.value,l.service,l.location,l.assigned,l.followUp,l.notes].map(v=>`"${v||''}"`).join(','));
      const csv = [headers.join(','),...rows].join('\n');
      const blob = new Blob([csv],{type:'text/csv'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href=url; a.download='leads_export.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('No leads data found'); }
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export Leads</div>
      <div className="tp-export-info">Downloads all your leads as a CSV file.</div>
      <div className="tp-export-btn" onClick={handleExport}>Download Leads CSV</div>
    </div>
  );
}

function ExportAll() {
  const handleExport = () => {
    const data = {
      exported: new Date().toISOString(),
      leads: JSON.parse(localStorage.getItem('ryzenos_leads')||'[]'),
      clients: JSON.parse(localStorage.getItem('ryzenos_clients')||'[]'),
    };
    const blob = new Blob([JSON.stringify(data,null,2)],{type:'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='ryzenos_backup.json'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export All Data</div>
      <div className="tp-export-info">Full backup including leads and clients as JSON.</div>
      <div className="tp-export-btn" onClick={handleExport}>Download Full Backup</div>
    </div>
  );
}

function QuickNotes() {
  const [notes, setNotes] = useState(()=>localStorage.getItem('ryzenos_notes')||'');
  const save = (v) => { setNotes(v); localStorage.setItem('ryzenos_notes',v); };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Quick Notes</div>
      <textarea className="tp-notes" placeholder="Jot anything down…" value={notes} onChange={e=>save(e.target.value)} rows={10}/>
      <div className="tp-notes-hint">Notes are saved automatically.</div>
    </div>
  );
}

function InvoiceGenerator({ onClose }) {
  const today = new Date();
  const dateStr = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${today.getFullYear()}`;
  const [form, setForm] = React.useState({
    invoiceNo: 'RF' + String(Date.now()).slice(-6),
    date: dateStr,
    clientName:'', clientCompany:'', clientAddress:'', clientPhone:'', clientEmail:'',
    service:'', description:'', amount:'', gst:'0',
  });
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));
  const subtotal = parseFloat(form.amount)||0;
  const gstAmt = subtotal*(parseFloat(form.gst)||0)/100;
  const total = subtotal+gstAmt;

  const clients = (() => {
    try { return JSON.parse(localStorage.getItem('ryzenos_clients')||'[]'); } catch { return []; }
  })();

  const selectClient = (e) => {
    const c = clients.find(cl=>cl.id===e.target.value);
    if(c) setForm(p=>({
      ...p,
      clientName: c.name || '',
      clientCompany: c.company || c.businessType || '',
      clientAddress: c.location || '',
      clientPhone: c.phone || '',
      clientEmail: c.email || '',
      service: c.service || p.service,
      amount: c.value ? String(c.value) : p.amount,
    }));
  };

  const handlePrint = () => {
    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>Invoice ${form.invoiceNo}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Plus Jakarta Sans',Arial,sans-serif;color:#101010;background:#fff;padding:0;font-size:13px}
.page{width:794px;min-height:1123px;margin:0 auto;padding:0;background:#fff;position:relative}
.header-bar{background:#FF6600;height:10px;width:100%}
.header{display:flex;justify-content:space-between;align-items:flex-start;padding:28px 36px 20px;border-bottom:1px solid #101010}
.logo-area{display:flex;align-items:center;gap:0}
.logo-img{height:32px}
.inv-right{text-align:right}
.inv-title{font-size:28px;font-weight:700;font-style:italic;color:#101010;letter-spacing:-.02em}
.inv-title span{color:#FF6600}
.inv-meta{font-size:13px;color:#101010;margin-top:4px}
.parties{display:grid;grid-template-columns:1fr 1fr;padding:24px 36px;gap:32px;border-bottom:1px solid #DFE4EA}
.party-lbl{font-size:14px;font-weight:700;color:#101010;margin-bottom:8px}
.party-name{font-size:13px;font-weight:700;margin-bottom:3px;color:#101010}
.party-det{font-size:12px;color:#101010;line-height:1.8}
.body{display:grid;grid-template-columns:1fr 1.4fr;padding:24px 36px;gap:28px;border-bottom:1px solid #DFE4EA}
.payment-box{background:#F9F9FA;border:1px solid #DFE4EA;border-radius:6px;padding:16px}
.pay-title{font-size:13px;font-weight:700;color:#FF6600;text-transform:uppercase;letter-spacing:.05em;margin-bottom:10px}
.pay-row{display:grid;grid-template-columns:1fr 1fr;font-size:12px;padding:4px 0;border-bottom:.5px solid #DFE4EA}
.pay-lbl{color:#666}
.pay-val{font-weight:700;color:#101010}
.table-section{}
table{width:100%;border-collapse:collapse}
.t-head{background:#fff}
th{padding:8px 0 8px 12px;text-align:left;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#101010;border-bottom:1px solid #DFE4EA}
th:last-child{text-align:right;padding-right:0}
td{padding:11px 0 11px 12px;font-size:13px;border-bottom:.5px solid #DFE4EA;color:#101010}
td:last-child{text-align:right;font-weight:600;padding-right:0}
td.desc-main{font-weight:600}
td.desc-sub{font-size:11px;color:#666;display:block;font-weight:400;margin-top:2px}
.totals{display:flex;flex-direction:column;gap:0;margin-top:10px}
.tot-line{display:flex;justify-content:space-between;padding:6px 0;font-size:13px;border-bottom:.5px solid #DFE4EA}
.tot-final-wrap{display:flex;justify-content:space-between;align-items:center;padding:10px 0 0;margin-top:6px}
.tot-lbl{font-size:14px;font-weight:700;text-transform:lowercase;color:#101010}
.tot-val{background:#FBA677;border-radius:4px;color:#FF6600;font-size:15px;font-weight:800;padding:8px 18px}
.note{padding:14px 36px;font-size:12px;font-style:italic;color:#666;border-bottom:.5px solid #DFE4EA}
.footer{display:flex;justify-content:space-between;align-items:center;padding:16px 36px;margin-top:auto}
.footer-thanks{font-size:18px;font-weight:800;font-style:italic;color:#101010}
.footer-info{font-size:11px;color:#101010;text-align:center}
.footer-in{width:28px;height:28px;background:#FF6600;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700;text-decoration:none}
</style></head><body>
<div class="page">
  <div class="header-bar"></div>
  <div class="header">
    <div class="logo-area">
      <svg width="32" height="32" viewBox="0 0 100 100"><polygon points="50,2 62,38 98,38 70,60 80,96 50,75 20,96 30,60 2,38 38,38" fill="#FF6600"/></svg>
      <span style="font-size:22px;font-weight:900;color:#FF6600;margin-left:6px;font-family:Plus Jakarta Sans,Arial,sans-serif">Ryzenforge</span>
    </div>
    <div class="inv-right">
      <div class="inv-title">Invoice | <span>${form.clientName||'Client'}</span></div>
      <div class="inv-meta">No. ${form.invoiceNo} | Date ${form.date}</div>
    </div>
  </div>
  <div class="parties">
    <div>
      <div class="party-lbl">From</div>
      <div class="party-name">Ryzenforge Enterprises LLP</div>
      <div class="party-det">Tower 2, DD Platinum Planet, Kathrikadav, Kaloor – 682017<br/>+91 81295 82113<br/>connect@ryzenforge.com<br/>www.ryzenforge.com<br/>www.ryzenforge.in</div>
    </div>
    <div>
      <div class="party-lbl">To</div>
      <div class="party-name">${form.clientName||'Client Name'}</div>
      <div class="party-det">${form.clientCompany||''}${form.clientAddress?'<br/>'+form.clientAddress:''}</div>
    </div>
  </div>
  <div class="body">
    <div class="payment-box">
      <div class="pay-title">Payment INFO</div>
      <div class="pay-row"><span class="pay-lbl">Account name</span><span class="pay-val">DHANUSH SANKAR</span></div>
      <div class="pay-row"><span class="pay-lbl">Acc No:</span><span class="pay-val">99980102312563</span></div>
      <div class="pay-row"><span class="pay-lbl">Bank:</span><span class="pay-val">FEDERAL BANK</span></div>
      <div class="pay-row" style="border:none"><span class="pay-lbl">IFSC:</span><span class="pay-val">FDRL0001316</span></div>
    </div>
    <div class="table-section">
      <table>
        <thead class="t-head">
          <tr><th>Description</th><th>Rate</th><th>GST</th><th>AMOUNT</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><span class="desc-main">${form.service||'Service'}</span>${form.description?'<span class="desc-sub">'+form.description+'</span>':''}</td>
            <td>Rs. ${subtotal.toLocaleString('en-IN')}</td>
            <td>${form.gst||0}%</td>
            <td>Rs. ${subtotal.toLocaleString('en-IN')}</td>
          </tr>
        </tbody>
      </table>
      <div class="totals">
        <div class="tot-line"><span>Sub total</span><span>Rs. ${subtotal.toLocaleString('en-IN')}</span></div>
        ${gstAmt>0?`<div class="tot-line"><span>GST (${form.gst}%)</span><span>Rs. ${gstAmt.toLocaleString('en-IN')}</span></div>`:''}
        <div class="tot-final-wrap">
          <span class="tot-lbl">total</span>
          <span class="tot-val">Rs╣${total.toLocaleString('en-IN')}/-</span>
        </div>
      </div>
    </div>
  </div>
  <div class="note">Work will commence upon receipt of the initial payment, as per the agreed terms.</div>
  <div class="footer">
    <div class="footer-thanks">Thank you!</div>
    <div class="footer-info">Ryzenforge Enterprises LLP | +91 81295 82113 | connect@ryzenforge.com</div>
    <a class="footer-in" href="https://linkedin.com/company/ryzenforge" target="_blank">in</a>
  </div>
</div>
</body></html>`);
    win.document.close();
    setTimeout(()=>win.print(),400);
  };

  return (
    <div className="tp-section">
      <div className="tp-sec-title">Invoice Generator</div>
      <div className="tp-inv-two">
        <div className="tp-inv-form">
          <div className="tp-inv-row">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Invoice Number</label><input className="tp-inv-inp" value={form.invoiceNo} onChange={e=>setF('invoiceNo',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Date</label><input className="tp-inv-inp" value={form.date} onChange={e=>setF('date',e.target.value)}/></div>
          </div>
          <div className="tp-inv-sec">Bill To</div>
          {clients.length>0 && (
            <div className="tp-inv-grp">
              <label className="tp-inv-lbl">Select from CRM</label>
              <select className="tp-inv-inp" onChange={selectClient} defaultValue="">
                <option value="">-- Select client or type manually --</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}{c.company?' — '+c.company:''}</option>)}
              </select>
            </div>
          )}
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Client Name</label><input className="tp-inv-inp" placeholder="e.g. John Doe" value={form.clientName} onChange={e=>setF('clientName',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Company Name</label><input className="tp-inv-inp" placeholder="e.g. VGDS Global" value={form.clientCompany} onChange={e=>setF('clientCompany',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Address (optional)</label><input className="tp-inv-inp" placeholder="Street, City, State" value={form.clientAddress} onChange={e=>setF('clientAddress',e.target.value)}/></div>
          <div className="tp-inv-sec">Service</div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Service Title</label><input className="tp-inv-inp" placeholder="e.g. Custom Website Development" value={form.service} onChange={e=>setF('service',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Description</label><input className="tp-inv-inp" placeholder="Brief description of work" value={form.description} onChange={e=>setF('description',e.target.value)}/></div>
          <div className="tp-inv-row">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Amount (INR)</label><input className="tp-inv-inp" type="number" placeholder="78000" value={form.amount} onChange={e=>setF('amount',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">GST %</label><input className="tp-inv-inp" type="number" placeholder="0" value={form.gst} onChange={e=>setF('gst',e.target.value)}/></div>
          </div>
          <div className="tp-inv-total-row">
            <span>Total</span><span className="tp-inv-total">Rs. {total.toLocaleString('en-IN')}</span>
          </div>
          <div className="tp-export-btn" style={{marginTop:8}} onClick={handlePrint}>Print / Download PDF</div>
        </div>
      </div>
    </div>
  );
}

export default function ToolsPanel({ onClose }) {
  const { theme } = useTheme();
  const [active, setActive] = useState('invoice');

  const renderContent = () => {
    if (active==='services')  return <ServiceTypes/>;
    if (active==='import')    return <ImportLeads/>;
    if (active==='export')    return <ExportLeads/>;
    if (active==='exportall') return <ExportAll/>;
    if (active==='notes')     return <QuickNotes/>;
    if (active==='invoice')   return <InvoiceGenerator onClose={onClose}/>;
    return null;
  };

  return (
    <div className="tp-wrap" data-theme={theme}>
      <div className="tp-overlay" onClick={onClose}/>
      <div className="tp-panel" data-theme={theme}>
        <div className="tp-header">
          <span className="tp-title">Tools</span>
          <div className="tp-close" onClick={onClose}>✕</div>
        </div>
        <div className="tp-body">
          <div className="tp-nav">
            {TOOLS.map(t=>(
              <div key={t.id} className={`tp-nav-item${active===t.id?' active':''}`} onClick={()=>setActive(t.id)}>
                <div>
                  <div className="tp-nav-label">{t.label}</div>
                  <div className="tp-nav-desc">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="tp-content">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
